import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react'
import {
  NotificationState,
  NotificationAction,
  NotificationActionType,
  NotificationContextValue,
  Notification,
  NotificationListParams,
  NotificationError,
} from '@/pages/NotificationCenter/types'
import {
  getNotificationTypeConfig,
  formatNotificationDate,
} from '@/pages/NotificationCenter/notificationHelpers'
import { notificationApi } from '@/services/NotificationCenter/notificationApi'
import { useNotificationPusher } from '@/pages/NotificationCenter/hooks/useNotificationPusher'

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  hasInitiallyLoaded: false,
  loading: {
    fetching: false,
    loadingMore: false,
    markingAsRead: false,
    deleting: false,
    bulkOperating: false,
  },
  error: null,
  pagination: {
    currentPage: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
  },
  filters: {},
  sort: {
    field: 'created_at',
    order: 'desc',
  },
  realtime: {
    connected: false,
    lastConnected: null,
    error: null,
    reconnectAttempts: 0,
  },
}

// ================================
// REDUCER
// ================================

/**
 * Notification reducer to handle state updates
 * Follows Redux-style patterns for predictable state management
 */
function notificationReducer(
  state: NotificationState,
  action: NotificationAction
): NotificationState {
  switch (action.type) {
    // Fetch notifications
    case NotificationActionType.FETCH_START:
      return {
        ...state,
        loading: { ...state.loading, fetching: true },
        error: null,
      }

    case NotificationActionType.FETCH_SUCCESS: {
      // Deduplicate notifications: merge new ones with existing ones from Pusher
      const existingIds = new Set(state.notifications.map(n => n.id))
      const newNotifications = action.payload.notifications.filter(
        n => !existingIds.has(n.id)
      )

      const allNotifications = [...state.notifications, ...newNotifications]

      // Sort notifications by created_at (newest first) to ensure correct order
      allNotifications.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

      return {
        ...state,
        notifications: allNotifications,
        hasInitiallyLoaded: true,
        pagination: { ...state.pagination, ...action.payload.pagination },
        loading: { ...state.loading, fetching: false },
        error: null,
      }
    }

    case NotificationActionType.FETCH_ERROR:
      return {
        ...state,
        hasInitiallyLoaded: true,
        loading: { ...state.loading, fetching: false },
        error: {
          message: action.payload.error,
          code: action.payload.code || null,
          retryable: action.payload.retryable || false,
        },
      }

    // Load more notifications
    case NotificationActionType.FETCH_MORE_START:
      return {
        ...state,
        loading: { ...state.loading, loadingMore: true },
      }

    case NotificationActionType.FETCH_MORE_SUCCESS: {
      // Deduplicate notifications to prevent duplicates during infinite scroll
      const existingIds = new Set(state.notifications.map(n => n.id))
      const newNotifications = action.payload.notifications.filter(
        n => !existingIds.has(n.id)
      )

      const allNotifications = [...state.notifications, ...newNotifications]

      // Don't calculate unread count from loaded notifications
      // Count remains unchanged during pagination - it represents TOTAL unread

      return {
        ...state,
        notifications: allNotifications,
        pagination: { ...state.pagination, ...action.payload.pagination },
        loading: { ...state.loading, loadingMore: false },
      }
    }

    case NotificationActionType.FETCH_MORE_ERROR:
      return {
        ...state,
        loading: { ...state.loading, loadingMore: false },
        error: {
          message: action.payload.error,
          code: action.payload.code || null,
          retryable: action.payload.retryable || false,
        },
      }

    // Mark as read
    case NotificationActionType.MARK_AS_READ_START:
      return {
        ...state,
        loading: { ...state.loading, markingAsRead: true },
      }

    case NotificationActionType.MARK_AS_READ_SUCCESS: {
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload.notification.id
          ? action.payload.notification
          : n
      )

      // Find the notification being marked as read
      const previousNotification = state.notifications.find(
        n => n.id === action.payload.notification.id
      )

      // Decrement count by 1 if the notification was previously unread
      const unreadCount =
        previousNotification?.status === 'unread'
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
        loading: { ...state.loading, markingAsRead: false },
      }
    }

    case NotificationActionType.MARK_AS_READ_ERROR:
      return {
        ...state,
        loading: { ...state.loading, markingAsRead: false },
        error: {
          message: action.payload.error,
          code: null,
          retryable: true,
        },
      }

    // Delete notification
    case NotificationActionType.DELETE_START:
      return {
        ...state,
        loading: { ...state.loading, deleting: true },
      }

    case NotificationActionType.DELETE_SUCCESS: {
      // Find the notification being deleted
      const deletedNotification = state.notifications.find(
        n => n.id === action.payload.notificationId
      )

      const updatedNotifications = state.notifications.filter(
        n => n.id !== action.payload.notificationId
      )

      // Decrement count by 1 if deleted notification was unread
      const unreadCount =
        deletedNotification?.status === 'unread'
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
        loading: { ...state.loading, deleting: false },
      }
    }

    case NotificationActionType.DELETE_ERROR:
      return {
        ...state,
        loading: { ...state.loading, deleting: false },
        error: {
          message: action.payload.error,
          code: null,
          retryable: true,
        },
      }

    // Mark all as read
    case NotificationActionType.MARK_ALL_AS_READ_START:
      return {
        ...state,
        loading: { ...state.loading, bulkOperating: true },
      }

    case NotificationActionType.MARK_ALL_AS_READ_SUCCESS: {
      const updatedNotifications = state.notifications.map(n =>
        n.status === 'unread'
          ? {
              ...n,
              status: 'read' as const,
              read_at: new Date().toISOString(),
            }
          : n
      )

      // Set count to 0 since all notifications are marked as read
      // This is optimistic - the actual count will be synced via API
      const unreadCount = 0

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
        loading: { ...state.loading, bulkOperating: false },
      }
    }

    case NotificationActionType.MARK_ALL_AS_READ_ERROR:
      return {
        ...state,
        loading: { ...state.loading, bulkOperating: false },
        error: {
          message: action.payload.error,
          code: null,
          retryable: true,
        },
      }

    // Rollback actions for optimistic updates
    case NotificationActionType.MARK_AS_READ_ROLLBACK: {
      // Restore the original notification state
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload.notification.id
          ? action.payload.notification
          : n
      )

      const unreadCount = updatedNotifications.filter(
        n => n.status === 'unread'
      ).length

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
        loading: { ...state.loading, markingAsRead: false },
      }
    }

    case NotificationActionType.MARK_ALL_AS_READ_ROLLBACK:
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.notifications.filter(
          n => n.status === 'unread'
        ).length,
        pagination: action.payload.pagination,
        loading: { ...state.loading, bulkOperating: false },
      }

    case NotificationActionType.DELETE_ROLLBACK:
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.notifications.filter(
          n => n.status === 'unread'
        ).length,
        pagination: action.payload.pagination,
        loading: { ...state.loading, deleting: false },
      }

    // Real-time updates
    case NotificationActionType.REALTIME_NOTIFICATION_RECEIVED: {
      // Add new notification to the beginning of the list
      const updatedNotifications = [
        action.payload.notification,
        ...state.notifications,
      ]

      // Increment count by 1 for new unread notification
      const unreadCount =
        action.payload.notification.status === 'unread'
          ? state.unreadCount + 1
          : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      }
    }

    case NotificationActionType.REALTIME_NOTIFICATION_READ: {
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload.notificationId
          ? { ...n, status: 'read' as const, read_at: action.payload.readAt }
          : n
      )

      // Check if the notification being marked as read is in our loaded list
      const notification = state.notifications.find(
        n => n.id === action.payload.notificationId
      )

      // Decrement count by 1 if notification was unread and is in our list
      const unreadCount =
        notification?.status === 'unread'
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      }
    }

    case NotificationActionType.REALTIME_NOTIFICATION_DELETED: {
      // Find the notification being deleted
      const deletedNotification = state.notifications.find(
        n => n.id === action.payload.notificationId
      )

      const updatedNotifications = state.notifications.filter(
        n => n.id !== action.payload.notificationId
      )

      // Decrement count by 1 if deleted notification was unread
      const unreadCount =
        deletedNotification?.status === 'unread'
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      }
    }

    case NotificationActionType.REALTIME_BULK_READ: {
      // Count how many unread notifications in our loaded list are affected
      const affectedUnreadCount = state.notifications.filter(
        n =>
          n.status === 'unread' && action.payload.notificationIds.includes(n.id)
      ).length

      const updatedNotifications = state.notifications.map(n =>
        action.payload.notificationIds.includes(n.id)
          ? {
              ...n,
              status: 'read' as const,
              read_at: new Date().toISOString(),
            }
          : n
      )

      // Decrement count by the number of affected notifications
      const unreadCount = Math.max(0, state.unreadCount - affectedUnreadCount)

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount,
      }
    }

    // Real-time connection
    case NotificationActionType.REALTIME_CONNECTED:
      return {
        ...state,
        realtime: {
          ...state.realtime,
          connected: true,
          lastConnected: new Date().toISOString(),
          error: null,
          reconnectAttempts: 0,
        },
      }

    case NotificationActionType.REALTIME_DISCONNECTED:
      return {
        ...state,
        realtime: {
          ...state.realtime,
          connected: false,
        },
      }

    case NotificationActionType.REALTIME_ERROR:
      return {
        ...state,
        realtime: {
          ...state.realtime,
          error: action.payload.error,
          reconnectAttempts: state.realtime.reconnectAttempts + 1,
        },
      }

    // Unread count
    case NotificationActionType.UPDATE_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload.count,
      }

    // Error handling
    case NotificationActionType.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }

    case NotificationActionType.SET_ERROR:
      return {
        ...state,
        error: {
          message: action.payload.error,
          code: action.payload.code || null,
          retryable: action.payload.retryable || false,
        },
      }

    // Reset to initial state (used on sign-out)
    case NotificationActionType.RESET:
      return initialState

    // Clear notifications (used before refresh to prevent merging)
    case NotificationActionType.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        pagination: {
          currentPage: 0,
          perPage: state.pagination.perPage,
          totalItems: 0,
          totalPages: 0,
          hasMore: true,
        },
      }

    default:
      return state
  }
}

// ================================
// CONTEXT CREATION
// ================================

const NotificationContext = createContext<NotificationContextValue | null>(null)

// ================================
// CONTEXT PROVIDER
// ================================

interface NotificationProviderProps {
  children: ReactNode
}

/**
 * NotificationProvider component
 *
 * This provider manages the notification center state and provides actions
 * for interacting with notifications. It is completely separate from the
 * ChatClientContext to ensure no interference with the existing chat system.
 *
 * During Phase 1, this provider sets up the foundation for notification
 * management without any actual API integration (that comes in later phases).
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState)

  // Track ongoing requests to prevent duplicates
  const ongoingRequests = React.useRef(new Set<string>())

  // ================================
  // PUSHER INTEGRATION
  // ================================

  // Integrate Pusher for real-time updates
  const pusherIntegration = useNotificationPusher(dispatch)

  // ================================
  // ACTION IMPLEMENTATIONS
  // ================================

  /**
   * Fetch total unread count from API
   * Used for initial load and periodic sync to ensure accuracy
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.getUnreadCount()
      dispatch({
        type: NotificationActionType.UPDATE_UNREAD_COUNT,
        payload: { count: response.unread_count },
      })
    } catch (error) {
      // Silently fail - don't disrupt user experience for count sync
      // The count will be synced on next successful operation
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch unread count:', error)
    }
  }, [])

  /**
   * Fetch notifications with optional parameters
   * Integrated with actual API in Phase 2
   */
  const fetchNotifications = useCallback(
    async (params?: NotificationListParams) => {
      // Prevent duplicate requests
      const requestKey = `fetch:${JSON.stringify(params || {})}`
      if (ongoingRequests.current.has(requestKey)) {
        return
      }

      ongoingRequests.current.add(requestKey)
      dispatch({ type: NotificationActionType.FETCH_START })

      try {
        const response = await notificationApi.getNotifications(params)

        dispatch({
          type: NotificationActionType.FETCH_SUCCESS,
          payload: {
            notifications: response.notifications,
            pagination: {
              currentPage: response.page,
              perPage: response.per_page,
              totalItems: response.total,
              totalPages: response.total_pages,
              hasMore: response.page < response.total_pages,
            },
          },
        })

        // Fetch total unread count separately to ensure accuracy
        // This is independent of loaded notifications
        await fetchUnreadCount()
      } catch (error) {
        const notificationError = error as NotificationError
        dispatch({
          type: NotificationActionType.FETCH_ERROR,
          payload: {
            error: notificationError.message || 'Failed to fetch notifications',
            code: notificationError.code,
            retryable: notificationError.retryable || false,
          },
        })
      } finally {
        ongoingRequests.current.delete(requestKey)
      }
    },
    [fetchUnreadCount]
  )

  /**
   * Load more notifications for pagination
   */
  const loadMore = useCallback(async () => {
    if (!state.pagination.hasMore || state.loading.loadingMore) return

    const nextPage = state.pagination.currentPage + 1
    const requestKey = `loadMore:${nextPage}`

    if (ongoingRequests.current.has(requestKey)) {
      return
    }

    ongoingRequests.current.add(requestKey)
    dispatch({ type: NotificationActionType.FETCH_MORE_START })

    try {
      const params: NotificationListParams = {
        page: nextPage,
        per_page: state.pagination.perPage,
        sort: 'created_at',
        order: 'desc',
      }

      const response = await notificationApi.getNotifications(params)

      dispatch({
        type: NotificationActionType.FETCH_MORE_SUCCESS,
        payload: {
          notifications: response.notifications,
          pagination: {
            currentPage: response.page,
            perPage: response.per_page,
            totalItems: response.total,
            totalPages: response.total_pages,
            hasMore: response.page < response.total_pages,
          },
        },
      })
    } catch (error) {
      const notificationError = error as NotificationError
      dispatch({
        type: NotificationActionType.FETCH_MORE_ERROR,
        payload: {
          error:
            notificationError.message || 'Failed to load more notifications',
          code: notificationError.code,
          retryable: notificationError.retryable || false,
        },
      })
    } finally {
      ongoingRequests.current.delete(requestKey)
    }
  }, [
    state.pagination.hasMore,
    state.pagination.currentPage,
    state.pagination.perPage,
    state.loading.loadingMore,
  ])

  /**
   * Mark a notification as read with optimistic updates
   */
  const markAsRead = useCallback(
    async (id: string) => {
      const notification = state.notifications.find(n => n.id === id)
      if (!notification || notification.status === 'read') {
        return
      }

      // Store original state for rollback
      const originalNotification = { ...notification }

      // Optimistic update
      const updatedNotification: Notification = {
        ...notification,
        status: 'read',
        read_at: new Date().toISOString(),
      }

      dispatch({
        type: NotificationActionType.MARK_AS_READ_SUCCESS,
        payload: { notification: updatedNotification },
      })

      try {
        // Make actual API call
        const response = await notificationApi.markAsRead(id)

        // Update with server response (in case server has different timestamp)
        dispatch({
          type: NotificationActionType.MARK_AS_READ_SUCCESS,
          payload: { notification: response },
        })

        // Sync count with server after successful mutation
        await fetchUnreadCount()
      } catch (error) {
        // Rollback optimistic update on failure
        dispatch({
          type: NotificationActionType.MARK_AS_READ_ROLLBACK,
          payload: { notification: originalNotification },
        })

        const notificationError = error as NotificationError
        dispatch({
          type: NotificationActionType.MARK_AS_READ_ERROR,
          payload: {
            error:
              notificationError.message ||
              'Failed to mark notification as read',
            code: notificationError.code,
            retryable: notificationError.retryable || false,
            notificationId: id,
          },
        })
      }
    },
    [state.notifications, fetchUnreadCount]
  )

  /**
   * Mark all notifications as read with optimistic updates
   */
  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = state.notifications.filter(
      n => n.status === 'unread'
    )
    if (unreadNotifications.length === 0) {
      return
    }

    // Store original notifications for rollback
    const originalNotifications = [...state.notifications]

    dispatch({ type: NotificationActionType.MARK_ALL_AS_READ_START })

    // Optimistic update
    dispatch({
      type: NotificationActionType.MARK_ALL_AS_READ_SUCCESS,
      payload: {
        count: unreadNotifications.length,
        message: 'Marking notifications as read...',
      },
    })

    try {
      // Make actual API call
      const response = await notificationApi.markAllAsRead()

      // Confirm with server response
      dispatch({
        type: NotificationActionType.MARK_ALL_AS_READ_SUCCESS,
        payload: {
          count: response.count,
          message: response.message,
        },
      })

      // Sync count with server after successful mutation
      await fetchUnreadCount()
    } catch (error) {
      // Rollback optimistic update on failure
      dispatch({
        type: NotificationActionType.MARK_ALL_AS_READ_ROLLBACK,
        payload: {
          notifications: originalNotifications,
          pagination: state.pagination,
        },
      })

      const notificationError = error as NotificationError
      dispatch({
        type: NotificationActionType.MARK_ALL_AS_READ_ERROR,
        payload: {
          error:
            notificationError.message ||
            'Failed to mark all notifications as read',
          code: notificationError.code,
          retryable: notificationError.retryable || false,
        },
      })
    }
  }, [state.notifications, state.pagination, fetchUnreadCount])

  /**
   * Delete a notification with optimistic updates
   */
  const deleteNotification = useCallback(
    async (id: string) => {
      const notification = state.notifications.find(n => n.id === id)
      if (!notification) {
        return
      }

      // Store original state for rollback
      const originalNotifications = [...state.notifications]

      dispatch({
        type: NotificationActionType.DELETE_START,
        payload: { notificationId: id },
      })

      // Optimistic update - remove notification immediately
      dispatch({
        type: NotificationActionType.DELETE_SUCCESS,
        payload: { notificationId: id },
      })

      try {
        // Make actual API call
        await notificationApi.deleteNotification(id)
        // Success - optimistic update was correct, no need to dispatch again

        // Sync count with server after successful mutation
        await fetchUnreadCount()
      } catch (error) {
        // Rollback optimistic update on failure
        dispatch({
          type: NotificationActionType.DELETE_ROLLBACK,
          payload: {
            notifications: originalNotifications,
            pagination: state.pagination,
          },
        })

        const notificationError = error as NotificationError
        dispatch({
          type: NotificationActionType.DELETE_ERROR,
          payload: {
            error: notificationError.message || 'Failed to delete notification',
            code: notificationError.code,
            retryable: notificationError.retryable || false,
            notificationId: id,
          },
        })
      }
    },
    [state.notifications, state.pagination, fetchUnreadCount]
  )

  // All filtering/sorting removed - simple list only

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: NotificationActionType.CLEAR_ERROR })
  }, [])

  /**
   * Refresh notifications (clears and reloads from first page)
   */
  const refresh = useCallback(async () => {
    // Clear existing notifications to prevent merging/duplicates
    dispatch({ type: NotificationActionType.CLEAR_NOTIFICATIONS })

    const params: NotificationListParams = {
      page: 1,
      per_page: state.pagination.perPage,
      sort: 'created_at',
      order: 'desc',
    }
    await fetchNotifications(params)
  }, [state.pagination.perPage, fetchNotifications])

  /**
   * Disconnect and reset notification system
   * Used on sign-out to clean up resources and state
   */
  const disconnect = useCallback(() => {
    // Clear API cache
    notificationApi.clearCache()

    // Disconnect Pusher service if available
    if (pusherIntegration.service) {
      pusherIntegration.service.disconnect()
    }

    // Reset state to initial
    dispatch({ type: NotificationActionType.RESET })
  }, [pusherIntegration.service])

  // ================================
  // SMART SYNC STRATEGY
  // ================================

  /**
   * Smart initial load strategy:
   * - Load initial data when Pusher is unavailable
   * - Sync when Pusher reconnects after being disconnected
   * - Avoid redundant API calls when Pusher is already providing data
   * - Don't attempt to load if user is not authenticated (service will be null)
   */
  useEffect(() => {
    const shouldPerformInitialLoad = () => {
      // Don't load if service is not available (user not authenticated)
      if (!pusherIntegration.service) {
        return false
      }

      // Don't load if we already have loaded data
      if (state.hasInitiallyLoaded) {
        return false
      }

      // Don't load if we're already fetching
      if (state.loading.fetching) {
        return false
      }

      // Check if Pusher is available and connected
      const { isPusherEnabled } = pusherIntegration.service

      // Load if Pusher is disabled or not connected
      if (!isPusherEnabled || !pusherIntegration.connected) {
        return true
      }

      // Load if we have no notifications and Pusher is connected
      // (this handles the case where Pusher is ready but we haven't loaded initial data)
      return state.notifications.length === 0
    }

    if (shouldPerformInitialLoad()) {
      fetchNotifications({
        page: 1,
        per_page: 20,
        sort: 'created_at',
        order: 'desc',
      })
    }
  }, [
    state.hasInitiallyLoaded,
    state.loading.fetching,
    state.notifications.length,
    pusherIntegration.service,
    pusherIntegration.connected,
    fetchNotifications,
  ])

  /**
   * Handle Pusher reconnection sync:
   * - Refresh data when Pusher reconnects after being disconnected
   * - This ensures we don't miss any notifications that occurred during disconnection
   * - Don't attempt to sync if user is not authenticated (service will be null)
   */
  useEffect(() => {
    const handleReconnectionSync = () => {
      // Don't sync if service is not available (user not authenticated)
      if (!pusherIntegration.service) {
        return
      }

      const { isPusherEnabled } = pusherIntegration.service

      // Only sync if:
      // 1. Pusher is enabled and connected
      // 2. We have previously loaded data (avoid double initial load)
      // 3. We're not already fetching
      if (
        isPusherEnabled &&
        pusherIntegration.connected &&
        state.hasInitiallyLoaded &&
        !state.loading.fetching
      ) {
        // Refresh the first page to catch any missed notifications
        fetchNotifications({
          page: 1,
          per_page: 20,
          sort: 'created_at',
          order: 'desc',
        })
      }
    }

    // Only trigger sync when connection state changes from false to true
    // (indicating a reconnection, not initial connection)
    if (
      pusherIntegration.service &&
      pusherIntegration.connected &&
      state.realtime.lastConnected &&
      state.hasInitiallyLoaded
    ) {
      handleReconnectionSync()
    }
  }, [
    pusherIntegration.service,
    pusherIntegration.connected,
    state.hasInitiallyLoaded,
    state.loading.fetching,
    state.realtime.lastConnected,
    fetchNotifications,
  ])

  // ================================
  // UTILITY FUNCTIONS
  // ================================

  /**
   * Get notification by ID
   */
  const getNotificationById = useCallback(
    (id: string): Notification | undefined =>
      state.notifications.find(n => n.id === id),
    [state.notifications]
  )

  /**
   * Get unread notifications
   */
  const getUnreadNotifications = useCallback(
    (): Notification[] =>
      state.notifications.filter(n => n.status === 'unread'),
    [state.notifications]
  )

  /**
   * Check if notification is expired
   */
  const isExpired = useCallback(
    (notification: Notification): boolean =>
      new Date(notification.expires_at) < new Date(),
    []
  )

  // ================================
  // CONTEXT VALUE
  // ================================

  const contextValue = useMemo<NotificationContextValue>(
    () => ({
      state: {
        ...state,
        realtime: {
          ...state.realtime,
          connected: pusherIntegration.connected,
        },
      },
      actions: {
        fetchNotifications,
        loadMore,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearError,
        refresh,
        disconnect,
      },
      utils: {
        getNotificationById,
        getUnreadNotifications,
        isExpired,
        formatDate: formatNotificationDate,
        getTypeConfig: getNotificationTypeConfig,
      },
    }),
    [
      state,
      pusherIntegration.connected,
      fetchNotifications,
      loadMore,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      clearError,
      refresh,
      disconnect,
      getNotificationById,
      getUnreadNotifications,
      isExpired,
    ]
  )

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  )
}

// ================================
// CUSTOM HOOK
// ================================

/**
 * Custom hook to access notification context
 *
 * @throws Error if used outside of NotificationProvider
 * @returns NotificationContextValue
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider. ' +
        'Make sure to wrap your component tree with NotificationProvider.'
    )
  }

  return context
}

/**
 * Default export for easier importing
 */
export default NotificationContext
